import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import Lottie from 'lottie-react';
import animationData from '../../assets/animations/Animation.json';
import CoursesList from '../CoursesList/CoursesList';
import PageTitle, { fetchSeoData } from '../common/PageTitle';
import Testimonials from '../testimonials/Testimonials';
import professionalismIcon from '../../assets/images/professionalism-icon.png';
import comprehensivenessIcon from '../../assets/images/comprehensiveness-icon.png';
import UserExperiences from '../UserExperiences';

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [homePageTitle, setHomePageTitle] = useState('منصة دوز التعليمية');
  const [siteDescription, setSiteDescription] = useState('وجهتك الأولى في تطوير مستواك');
  const [siteTitle, setSiteTitle] = useState('دوز التعليمية');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await api.publicCategories.getAll();
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        setCategories([]);
      }
    };

    const loadSeoData = async () => {
      try {
        const data = await fetchSeoData();
        setHomePageTitle(data.homePageTitle);
        setSiteDescription(data.siteDescription);
        setSiteTitle(data.siteTitle);
      } catch (error) {
        console.error('Error loading SEO data:', error);
      }
    };

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    
    fetchCategories();
    loadSeoData();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <PageTitle title="الرئيسية" />
      
      {/* Hero Section */}
      <div className="section-wrapper -mt-8 md:-mt-12 lg:-mt-16">
        <section className="section-spots pink min-h-[300px] flex items-center py-6">
          <div className="section-content w-full">
            <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-between gap-4 h-full">
              <div className="flex-1 flex justify-center order-1 lg:order-2">
                <Lottie
                  animationData={animationData}
                  className="w-[400px] md:w-[500px] lg:w-[600px] h-auto"
                  loop={true}
                />
              </div>
              <div className="flex-1 text-center lg:text-right order-2 lg:order-1">
                <h1 className="text-2xl md:text-3xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  {homePageTitle}
                </h1>
                <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                  {siteDescription}
                </p>
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-3 text-base font-medium text-black bg-gradient-to-l from-[#fad94a] to-[#f4bf49] hover:from-[#d4b83f] hover:to-[#cfa13d] rounded-full transition-all duration-200"
                >
                  انضم إلى {siteTitle}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Courses Section */}
      <section className="mt-0 py-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12 relative">
            الدورات التدريبية
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 -mb-4 w-16 h-1 bg-primary-500 rounded-full" />
          </h2>
          <CoursesList isMobile={isMobile} />
        </div>
      </section>

      {/* User Experiences */}
      <section className="mt-12 py-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12 relative">
          تجارب مشتركينا
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 -mb-4 w-16 h-1 bg-primary-500 rounded-full" />
          </h2>
          <UserExperiences />
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-10 dark:bg-gray-900 mt-12 py-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12 relative">
            ماذا يميز منصة دوز؟
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 -mb-4 w-16 h-1 bg-primary-500 rounded-full" />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="cursor-pointer border border-gray-200 dark:border-gray-700 rounded-2xl">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 flex flex-col items-center text-center shadow-md hover:shadow-lg transition-shadow duration-300 h-full">
                <div className="w-16 h-16 mb-6">
                  <img src={comprehensivenessIcon} alt="شمولية المحتوى" className="w-full h-full" />
                </div>
                <h3 className="text-xl font-semibold text-primary-600 dark:text-primary-400 mb-4">شمولية المحتوى</h3>
                <p className="text-gray-600 dark:text-gray-300">نقدم محتوى تعليمي شامل ومتكامل يغطي جميع احتياجاتك التعليمية</p>
              </div>
            </div>

            <div className="cursor-pointer border border-gray-200 dark:border-gray-700 rounded-2xl">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 flex flex-col items-center text-center shadow-md hover:shadow-lg transition-shadow duration-300 h-full">
                <div className="w-16 h-16 mb-6">
                  <img src={professionalismIcon} alt="احترافية التدريب" className="w-full h-full" />
                </div>
                <h3 className="text-xl font-semibold text-primary-600 dark:text-primary-400 mb-4">احترافية التدريب</h3>
                <p className="text-gray-600 dark:text-gray-300">نقدم تدريباً احترافياً على يد خبراء متخصصين في مجالاتهم</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fields Section */}
      <section className="mt-12 py-0">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12 relative">
            مجالات دوز
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 -mb-4 w-16 h-1 bg-primary-500 rounded-full" />
          </h2>
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((category, index) => (
                  <div key={category.id} className={`${categories.length === 1 ? 'lg:col-start-2 sm:col-start-1' : ''} cursor-pointer border border-gray-200 dark:border-gray-700 rounded-lg`}>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex flex-col items-center justify-center text-center shadow-md hover:shadow-lg transition-shadow duration-300 min-h-[100px] h-full">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                        {category.name}
                      </h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />
    </div>
  );
};

export default HomePage;
